import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { blobticMessage } from '../components/BlobticMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { SecurityInfoDialog } from './SecurityInfoDialog';

interface DoubleAuthDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DoubleAuthDialog: React.FC<DoubleAuthDialogProps> = ({ open, onClose }) => {
  const [otpCode, setOtpCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [attempts, setAttempts] = useState(0); // Compteur de tentatives
  const [showSecurityInfo, setShowSecurityInfo] = useState(false); // État pour le dialogue d'info
  const { completeDoubleAuth, cancelDoubleAuth, error, clearError, pendingCredentials, login } = useAuth();
  
  const { t, dir } = useLanguage();

  // Compteur pour le renvoi du code
  useEffect(() => {
    if (open && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [open, countdown]);

  // Réinitialiser l'état quand le dialogue s'ouvre
  useEffect(() => {
    if (open) {
      setOtpCode('');
      setIsSubmitting(false);
      setCountdown(60); // 60 secondes avant de pouvoir renvoyer
      setAttempts(0); // Réinitialiser le compteur de tentatives
      clearError();
    }
  }, [open, clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otpCode.trim()) {
      blobticMessage.error(t('doubleAuth.error.required'), t('doubleAuth.error.requiredDesc'));
      return;
    }

    if (otpCode.length !== 6) {
      blobticMessage.error(t('doubleAuth.error.invalid'), t('doubleAuth.error.invalidDesc'));
      return;
    }

    setIsSubmitting(true);
    setAttempts(prev => prev + 1); // Incrémenter le compteur de tentatives

    try {
      const success = await completeDoubleAuth(otpCode);
      if (success) {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    cancelDoubleAuth();
    onClose();
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setIsSubmitting(true);
    try {
      // Utiliser les credentials en attente pour déclencher un nouvel envoi d'OTP
      if (pendingCredentials) {
        // Réessayer la connexion pour déclencher un nouvel envoi d'OTP
        const success = await login(pendingCredentials);
        if (success) {
          // Si la connexion réussit, le dialogue se fermera automatiquement
          return;
        }
      }
      setCountdown(60);
      // Notification de succès pour le renvoi
      blobticMessage.success(t('doubleAuth.success.resend'), t('doubleAuth.success.resendDesc'));
    } catch (error) {
      console.error('Erreur lors du renvoi du code:', error);
      blobticMessage.error(t('doubleAuth.error.resend'), t('doubleAuth.error.resendDesc'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Dialog
        dir={dir}
        open={open}
        onClose={() => { }} // Empêche la fermeture en cliquant en dehors
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '8px'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6" component="div">
              {t('doubleAuth.title')}
            </Typography>
          </Box>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              {t('doubleAuth.body')}
            </Typography>

            {/* Informations de sécurité */}
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('doubleAuth.security.label')}</strong> {t('doubleAuth.security.desc')}
                <Button
                  size="small"
                  color="primary"
                  onClick={() => setShowSecurityInfo(true)}
                  sx={{ ml: 1, textTransform: 'none' }}
                >
                  ℹ️ {t('doubleAuth.btn.moreInfo')}
                </Button>
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {error}
                </Typography>
                {error.includes('bloqué') && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    💡 <strong>{t('doubleAuth.tip.label')}</strong> {t('doubleAuth.alert.blockedDesc')}
                  </Typography>
                )}
                {error.includes('expiré') && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem' }}>
                    💡 <strong>{t('doubleAuth.tip.label')}</strong> {t('doubleAuth.alert.expiredDesc')}
                  </Typography>
                )}
              </Alert>
            )}

            <TextField
              fullWidth
              label={t('doubleAuth.code.label')}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder={t('doubleAuth.code.placeholder')}
              inputProps={{
                maxLength: 6,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem'
                }
              }}
              sx={{ mb: 2 }}
              disabled={isSubmitting}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ⏰ <strong>{t('doubleAuth.delay.label')}</strong> {t('doubleAuth.delay.wait')}{' '}
              <span style={{
                fontWeight: 'bold',
                color: countdown === 0 ? '#1976d2' : '#666',
                backgroundColor: countdown === 0 ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                padding: countdown === 0 ? '2px 6px' : '0',
                borderRadius: countdown === 0 ? '4px' : '0'
              }}>
                {countdown === 0 ? t('doubleAuth.delay.now') : formatTime(countdown)}
              </span>
              <br />
              <small style={{ color: '#666', fontSize: '0.75rem' }}>
                🔒 <strong>{t('doubleAuth.delay.protectionLabel')}</strong> {t('doubleAuth.delay.protectionDesc')}
              </small>
              {attempts > 0 && (
                <>
                  <br />
                  <small style={{
                    color: attempts >= 2 ? '#d32f2f' : '#f57c00',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    ⚠️ {t('doubleAuth.attempts.label')} {attempts}/3 - {attempts >= 2 ? t('doubleAuth.attempts.lastChance') : t('doubleAuth.attempts.remaining')}
                  </small>
                </>
              )}
            </Typography>

            <DialogActions sx={{ px: 0, pt: 2 }}>
              <Button
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{ minWidth: 100 }}
              >
                {t('btn.cancel')}
              </Button>

              <Button
                onClick={handleResendCode}
                disabled={isSubmitting || countdown > 0}
                variant="outlined"
                sx={{ minWidth: 120 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} />
                ) : (
                  t('doubleAuth.btn.resend')
                )}
              </Button>

              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !otpCode.trim()}
                sx={{ minWidth: 100 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t('doubleAuth.btn.verify')
                )}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Dialogue d'information sur la sécurité */}
      <SecurityInfoDialog
        open={showSecurityInfo}
        onClose={() => setShowSecurityInfo(false)}
      />
    </>
  );
};
